/**
 * Moved from the web's `src/utils/eventNavigation.js`.
 *
 * The pure half only: turning a destination path plus an event id into the URL the app opens.
 * `openEventInNamedWindow` stays on the web — it needs `window` and the navigation-diagnostics
 * writer, neither of which exists here — and `src/utils/eventNavigation.ts` re-exports these so
 * every existing web import keeps working unchanged.
 *
 * It moved because two consumers now need the same answer: the web's `useNavToEvent`, and
 * `notificationLink`, which builds the absolute URL emailed out with a notification. Those two
 * must agree exactly — a hand-built `/event/…` path that matched no route is how a notification
 * deep link broke before — so there is one implementation and both call it.
 *
 * This is the single source of truth for event URLs. `functions/src/utils/staffEventLink.ts`
 * keeps an inlined copy (functions cannot runtime-import `@otm/shared` — see that file's header
 * comment) — change both when this changes.
 */

/**
 * `URLSearchParams` is a real global in every runtime that can reach this module (browsers
 * natively, React Native via its own polyfill) but lives in the `DOM` lib the packages
 * deliberately do not pull in — see `packages/hooks/src/globals.d.ts` for the same reasoning.
 * Declared module-locally rather than as an ambient global so it cannot collide with a consumer
 * whose program *does* include DOM types. Only the four members used below are declared.
 */
declare const URLSearchParams: {
  new (init?: string): {
    get(name: string): string | null;
    set(name: string, value: string): void;
    delete(name: string): void;
    toString(): string;
  };
};

export const EVENT_QUERY_PARAM = 'id';

export const isEventPath = (pathname = ''): boolean => {
  if (typeof pathname !== 'string') return false;
  return pathname === '/event' || pathname === '/event/' || pathname.startsWith('/event/');
};

interface SplitDestination {
  pathname: string;
  search: string;
  hash: string;
}

const splitPathSearchHash = (destination = '/event/'): SplitDestination => {
  const rawDestination = destination || '/event/';
  const hashIndex = rawDestination.indexOf('#');
  const withoutHash = hashIndex >= 0 ? rawDestination.slice(0, hashIndex) : rawDestination;
  const hash = hashIndex >= 0 ? rawDestination.slice(hashIndex) : '';
  const searchIndex = withoutHash.indexOf('?');
  const rawPathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  const pathname = rawPathname.startsWith('/') ? rawPathname : `/${rawPathname}`;

  return {
    pathname: pathname === '/event' ? '/event/' : pathname,
    search,
    hash
  };
};

export const getEventIdFromSearch = (search = ''): string => {
  const params = new URLSearchParams(search || '');
  return params.get(EVENT_QUERY_PARAM) || '';
};

export const withEventSearch = (destination = '/event/', eventId?: string | null): string => {
  const { pathname, search, hash } = splitPathSearchHash(destination);

  if (!eventId || !isEventPath(pathname)) {
    return `${pathname}${search}${hash}`;
  }

  const params = new URLSearchParams(search || '');
  params.set(EVENT_QUERY_PARAM, eventId);
  const query = params.toString();

  return `${pathname}${query ? `?${query}` : ''}${hash}`;
};

export const preserveEventSearch = (destination = '/event/', currentSearch = ''): string => {
  const eventId = getEventIdFromSearch(currentSearch);
  return withEventSearch(destination, eventId);
};

export const getCurrentEventUrl = (
  location: { pathname?: string; search?: string; hash?: string } | null | undefined,
  eventId?: string | null
): string => {
  const pathname = location?.pathname || '/event/';
  const search = location?.search || '';
  const hash = location?.hash || '';
  return withEventSearch(`${pathname}${search}${hash}`, eventId);
};
